import { useEffect, useState } from "react";
import { Certificate, UserPlus, UsersThree } from "@phosphor-icons/react";
import { toast } from "sonner";

import { LoadingState, MetricGrid, StatusPill } from "@/components/DashboardParts";
import PersonBuilder from "@/components/PersonBuilder";
import BatchAttendanceReport from "@/components/BatchAttendanceReport";
import TrainerAttendanceCheckIn from "@/components/TrainerAttendanceCheckIn";
import RoleLayout from "@/components/RoleLayout";
import { api, formatApiError } from "@/lib/api";

export default function TrainerDashboard() {
  const [dashboard, setDashboard] = useState(null);
  const [learners, setLearners] = useState([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showLearnerForm, setShowLearnerForm] = useState(false);
  const [batches, setBatches] = useState([]);

  const refresh = async () => {
    const [dashboardResponse, learnerResponse, batchResponse] = await Promise.all([api.get("/lms/dashboard"), api.get("/lms/learners"), api.get("/lms/batches")]);
    setDashboard(dashboardResponse.data);
    setLearners(learnerResponse.data);
    setBatches(batchResponse.data);
  };

  useEffect(() => { refresh(); }, []);

  const issueCertificate = async (courseId, learnerId) => {
    try {
      await api.post(`/lms/courses/${courseId}/certificate`, { learner_id: learnerId });
      toast.success("Certificate issued");
      refresh();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const downloadManual = async () => {
    setIsDownloading(true);
    try {
      const response = await api.get("/lms/trainer/user-manual", { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = "Skillexpress_Trainer_User_Manual.pdf";
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) { toast.error(formatApiError(error)); } finally { setIsDownloading(false); }
  };

  if (!dashboard) {
    return <LoadingState label="Loading your trainer workspace" />;
  }

  const metrics = [
    { key: "courses", label: "Active courses", value: dashboard.metrics.courses, hint: "Assigned to you" },
    { key: "learners", label: "Learners", value: dashboard.metrics.learners, hint: "Across your courses" },
    { key: "progress", label: "Completion rate", value: `${dashboard.metrics.completion_rate}%`, hint: "Current cohort" },
    { key: "reviews", label: "Quiz reviews", value: dashboard.metrics.pending_reviews, hint: "Ready to review" },
  ];

  return (
    <RoleLayout eyebrow="Trainer workspace" title="Learning delivery">
      <TrainerAttendanceCheckIn />
      <div className="manual-download-row"><span>Need a quick refresher?</span><button className="outline-button" onClick={downloadManual} disabled={isDownloading} data-testid="download-trainer-manual-button">{isDownloading ? "Preparing manual" : "Download trainer manual"}</button></div>
      <MetricGrid items={metrics} testPrefix="trainer" />
      <section className="section-heading" id="courses" data-testid="trainer-courses-section"><div><p className="eyebrow">Your delivery</p><h2>Assigned courses</h2></div></section>
      <section className="course-list-table" data-testid="trainer-courses-table">
        {dashboard.courses.map((course) => <article key={course.id} data-testid={`trainer-course-${course.id}`}><div><strong>{course.title}</strong><span>{course.category} · {course.duration}</span></div><StatusPill positive>{course.status}</StatusPill></article>)}
      </section>
      <section className="section-heading" data-testid="trainer-create-learner-section"><div><p className="eyebrow">Build your cohort</p><h2>Create learner account</h2></div><button className="primary-button compact-button" onClick={() => setShowLearnerForm(!showLearnerForm)} data-testid="toggle-create-learner-button"><UserPlus aria-hidden="true" size={20} />{showLearnerForm ? "Close form" : "New learner"}</button></section>
      {showLearnerForm && <PersonBuilder endpoint="/lms/learners" personLabel="Learner" testPrefix="create-learner" assignmentOptions={batches.filter((batch) => batch.available_seats > 0).map((batch) => ({ id: batch.id, label: `${batch.name} · ${batch.course_title} · ${batch.available_seats} seats` }))} assignmentLabel="Assign to batch" assignmentField="batch_id" onCreated={() => { setShowLearnerForm(false); refresh(); }} />}
      <section className="section-heading" id="learners" data-testid="trainer-learners-section"><div><p className="eyebrow">Cohort insight</p><h2>Learner progress</h2></div><UsersThree aria-hidden="true" size={27} /></section>
      <section className="data-table-wrap"><table data-testid="trainer-learners-table"><thead><tr><th>Learner</th><th>Course</th><th>Quiz</th><th>Certificate</th></tr></thead><tbody>{learners.flatMap((learner) => learner.enrollments.map((enrollment) => <tr key={enrollment.id} data-testid={`trainer-enrollment-${enrollment.id}`}><td><strong>{learner.name}</strong><span>{learner.email}</span></td><td>{dashboard.courses.find((course) => course.id === enrollment.course_id)?.title || "Course"}</td><td>{enrollment.quiz_score === null ? "Not submitted" : `${enrollment.quiz_score}%`}</td><td>{enrollment.certificate_issued ? <StatusPill positive>Issued</StatusPill> : enrollment.quiz_score >= 70 ? <button className="table-action" onClick={() => issueCertificate(enrollment.course_id, learner.id)} data-testid={`issue-certificate-${enrollment.id}`}><Certificate aria-hidden="true" size={18} />Issue</button> : <StatusPill>Awaiting pass</StatusPill>}</td></tr>))}</tbody></table></section>
      <BatchAttendanceReport batches={batches} />
    </RoleLayout>
  );
}